import React, { useCallback, useEffect, useRef, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import lottie from "lottie-web";
import { Helmet } from "react-helmet-async";

import ProductAd from "../components/ProductAd";
import Footer from "../components/Footer";
import BleepSpinnerDark from "../components/lottie/BleepSpinnerDark.json";

const PAGE_SIZE = 16;

// Verified listings. Same furniture as /bleeps and /search: dark band, title
// block, one grid, skeletons on first load.
const Store = ({ domain }) => {
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const fetchingRef = useRef(false);
  const spinnerRef = useRef(null);

  const buildUrl = (pageNumber) =>
    `/api/genuineProducts?page=${pageNumber}&size=${PAGE_SIZE}`;

  /* ---------------- First page -------------------------------------------- */
  const loadFirstPage = useCallback(async () => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    setIsLoading(true);
    try {
      const res = await fetch(buildUrl(1));
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      setProducts(list);
      setHasMore(list.length === PAGE_SIZE);
      setPage(2);
    } catch (err) {
      console.error("Error fetching genuine products:", err);
      setHasMore(false);
    } finally {
      setIsLoading(false);
      fetchingRef.current = false;
    }
  }, []);

  useEffect(() => {
    loadFirstPage();
  }, [loadFirstPage]);

  /* ---------------- Subsequent pages -------------------------------------- */
  const fetchMore = async () => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    try {
      const res = await fetch(buildUrl(page));
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      setProducts((prev) => [...prev, ...list]);
      setHasMore(list.length === PAGE_SIZE);
      setPage((prev) => prev + 1);
    } catch (err) {
      console.error("Error fetching genuine products:", err);
      setHasMore(false);
    } finally {
      fetchingRef.current = false;
    }
  };

  /* ---------------- Spinner ------------------------------------------------ */
  // No dependency array: the container only exists once InfiniteScroll renders
  // its loader, which is well after mount.
  useEffect(() => {
    if (spinnerRef.current && !spinnerRef.current._loaded) {
      spinnerRef.current._loaded = true;
      lottie.loadAnimation({
        container: spinnerRef.current,
        renderer: "canvas",
        loop: true,
        autoplay: true,
        animationData: BleepSpinnerDark,
      });
    }
  });

  return (
    <div className="bleep-light bl-page bl-page--dark">
      <Helmet>
        <title>Genuine Products | BLEEP</title>
        <meta
          name="description"
          content="Browse genuine products from verified sellers and contact vendors directly."
        />
      </Helmet>

      <section className="bl-section bl-section--dark">
        <div className="bl-shell bl-feed-head">
          <h1 className="bl-page-title">Buy genuine</h1>
          <p className="bl-page-sub">
            Verified listings from trusted sellers. Contact any of them
            directly.
          </p>
        </div>

        <div className="bl-shell">
          {isLoading && products.length === 0 ? (
            <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 g-3">
              {Array.from({ length: PAGE_SIZE }).map((_, i) => (
                <div
                  className="col pb-3"
                  key={i}
                >
                  <div className="bleep-post-skeleton bleep-post-skeleton--square bl-pulse">
                    <div className="bleep-post-skeleton__media" />
                    <div className="bleep-post-skeleton__body">
                      <div className="bleep-post-skeleton__line" />
                      <div
                        className="bleep-post-skeleton__line"
                        style={{ width: "60%" }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="bl-empty">
              <div className="bl-empty__icon">
                <i
                  className="fas fa-store"
                  aria-hidden="true"
                />
              </div>
              <h2 className="bl-empty__title">No listings yet</h2>
              <p className="bl-empty__text">
                Verified sellers have not listed anything here yet. Check back
                soon.
              </p>
            </div>
          ) : (
            <InfiniteScroll
              dataLength={products.length}
              next={fetchMore}
              hasMore={hasMore}
              style={{ overflow: "unset" }}
              loader={
                <div
                  style={{ display: "flex", justifyContent: "center" }}
                  aria-label="Loading more products"
                >
                  <div
                    ref={spinnerRef}
                    style={{ width: 140, height: 140 }}
                  />
                </div>
              }
              endMessage={
                <p className="bl-end-message">
                  That's every listing <i className="fas fa-check" />
                </p>
              }
            >
              <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 g-3">
                {products.map((p) => (
                  <ProductAd
                    key={p._id}
                    product={p}
                  />
                ))}
              </div>
            </InfiniteScroll>
          )}
        </div>
      </section>

      <Footer domain={domain} />
    </div>
  );
};

export default Store;
